import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { logoutUser, getNotifications } from "../../firebase/services";
import { useAuth } from "../../hooks/useAuth";
import { useLanguage } from "../../hooks/useLanguage";
import ChangePasswordModal from "../../components/ChangePasswordModal";
import LangSwitcher, { LangInline } from "../../components/LangSwitcher";

const NavItem = ({ to, icon, label, end, badge }) => (
  <NavLink to={to} end={end} className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}>
    <span style={{ fontSize: 16, position: "relative", display: "inline-flex" }}>
      {icon}
      {badge > 0 && (
        <span style={{
          position: "absolute", top: -4, right: -6,
          background: "#E24B4A", color: "#fff",
          borderRadius: "50%", fontSize: 9, fontWeight: 700,
          minWidth: 14, height: 14, display: "flex",
          alignItems: "center", justifyContent: "center", padding: "0 2px",
          lineHeight: 1,
        }}>{badge > 99 ? "99+" : badge}</span>
      )}
    </span>
    <span>{label}</span>
  </NavLink>
);

const TabItem = ({ to, icon, label, end, badge }) => (
  <NavLink to={to} end={end} className={({ isActive }) => "bottom-tab-item" + (isActive ? " active" : "")}>
    <span className="tab-icon" style={{ position: "relative", display: "inline-flex" }}>
      {icon}
      {badge > 0 && (
        <span style={{
          position: "absolute", top: -4, right: -6,
          background: "#E24B4A", color: "#fff",
          borderRadius: "50%", fontSize: 9, fontWeight: 700,
          minWidth: 14, height: 14, display: "flex",
          alignItems: "center", justifyContent: "center", padding: "0 2px",
          lineHeight: 1,
        }}>{badge > 99 ? "99+" : badge}</span>
      )}
    </span>
    <span>{label}</span>
  </NavLink>
);

export default function PromoterLayout() {
  const { profile, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showChangePw, setShowChangePw] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    const key = `promoterhq_notif_read_${user.uid}`;
    const lastRead = Number(localStorage.getItem(key) || 0);
    getNotifications(user.uid).then(notifs => {
      const count = notifs.filter(n => {
        const ts = n.createdAt?.toDate?.()?.getTime?.() ?? 0;
        return ts > lastRead;
      }).length;
      setUnreadCount(count);
    });
  }, [user]);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-logo">PromoterHQ <span>{profile?.name}</span></div>
        <nav style={{ flex: 1 }}>
          <div className="sidebar-section">
            <NavItem to="/promoter" end icon="🏠" label={t("nav.home")} />
            <NavItem to="/promoter/checkin" icon="⏰" label={t("nav.checkin")} />
            <NavItem to="/promoter/sales" icon="🛒" label={t("nav.my_sales")} />
            <NavItem to="/promoter/reports" icon="📈" label={t("nav.reports")} />
            <NavItem to="/promoter/programs" icon="🎁" label={t("nav.bonus_programs")} />
            <NavItem to="/promoter/sku" icon="💻" label={t("nav.sku_lookup")} />
            <NavItem to="/promoter/training" icon="📚" label={t("nav.training")} />
            <NavItem to="/promoter/notifications" icon="🔔" label={t("nav.notifications")} badge={unreadCount} />
          </div>
        </nav>
        <div className="sidebar-footer">
          <div style={{ padding: "0 0 8px 0" }}>
            <LangSwitcher />
          </div>
          <div className="profile-menu-wrap">
            <button className="profile-menu-btn" onClick={() => setMenuOpen(o => !o)}>
              <span className="profile-avatar">👤</span>
              <span className="profile-name">{profile?.shopName}</span>
              <span style={{ marginLeft: "auto", fontSize: 10 }}>{menuOpen ? "▲" : "▼"}</span>
            </button>
            {menuOpen && (
              <div className="profile-dropdown">
                <button className="profile-dropdown-item" onClick={() => { setShowChangePw(true); setMenuOpen(false); }}>
                  🔑 {t("auth.change_pw")}
                </button>
                <div className="profile-dropdown-divider" />
                <button className="profile-dropdown-item profile-dropdown-danger" onClick={logoutUser}>
                  🚪 {t("auth.logout")}
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="page-container"><Outlet /></div>
      </main>

      <div className="bottom-tab-bar">
        <div className="bottom-tab-bar-inner">
          <TabItem to="/promoter" end icon="🏠" label={t("nav.home")} />
          <TabItem to="/promoter/checkin" icon="⏰" label="Check-in" />
          <TabItem to="/promoter/sales" icon="🛒" label={t("nav.sales")} />
          <TabItem to="/promoter/notifications" icon="🔔" label={t("nav.notifications")} badge={unreadCount} />
          <button className="bottom-tab-item" onClick={() => setShowMore(true)}>
            <span className="tab-icon">☰</span>
            <span>{t("nav.more")}</span>
          </button>
        </div>
      </div>

      {showMore && (
        <>
          <div className="more-menu-overlay" onClick={() => setShowMore(false)} />
          <div className="more-menu-sheet">
            <div className="more-menu-handle" />
            <button className="more-menu-item" onClick={() => { navigate("/promoter/reports"); setShowMore(false); }}>
              <span className="more-icon">📈</span>{t("nav.reports")}
            </button>
            <button className="more-menu-item" onClick={() => { navigate("/promoter/programs"); setShowMore(false); }}>
              <span className="more-icon">🎁</span>{t("nav.bonus_programs")}
            </button>
            <button className="more-menu-item" onClick={() => { navigate("/promoter/sku"); setShowMore(false); }}>
              <span className="more-icon">💻</span>{t("nav.sku_lookup")}
            </button>
            <button className="more-menu-item" onClick={() => { navigate("/promoter/training"); setShowMore(false); }}>
              <span className="more-icon">📚</span>{t("nav.training")}
            </button>
            <div style={{ padding: "8px 20px" }}><LangInline /></div>
            <div style={{ height: 1, background: "#f0f0f0", margin: "8px 0" }} />
            <button className="more-menu-item" onClick={() => { setShowChangePw(true); setShowMore(false); }}>
              <span className="more-icon">🔑</span>{t("auth.change_pw")}
            </button>
            <button className="more-menu-item" style={{ color: "#dc2626" }} onClick={logoutUser}>
              <span className="more-icon">🚪</span>{t("auth.logout")}
            </button>
          </div>
        </>
      )}

      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
